import translations from './i18n.json';
import { translate, translateInComponent } from 'infras/i18n';

import { useState, useContext, useMemo, SyntheticEvent } from 'react';

import {
  apiPath,
  dashboardFormAnswerListExportPath,
  dashboardFormListPath,
  dashboardPath,
  formEditPath,
  formNewPath,
  formPath,
  loginPath,
  webPath,
} from 'presentations/routes/path';

export enum FormOptionIndex {
  edit = 0,
  copy = 1,
  delete = 2,
  divider = 3,
  activation = 4,
}

export enum FormShareOptionIndex {
  addToWebsite = 0,
  divider1 = 1,
  export = 2,
  divider2 = 3,
  share = 4,
}

import { destroy, get, patch, post } from 'infras/rest_client';
import { ViewerItem, UserProfileItem } from 'presentations/api/schema/user';
import {
  FormCreateResult,
  FormListItem,
  FormUpdateBody,
  SortType,
} from 'presentations/api/schema/form';
import { useRequest } from 'presentations/views/hooks/request';
import { useRouter } from 'next/router';
import {
  DashboardPortalContext,
  setErrorMessage,
  setSuccessMessage,
} from 'presentations/reducers/DashboardPortal';
import { setEvent } from 'presentations/views/helpers/analytics';
import { currentTimeZone } from 'infras/datetime';
import { TeamUserItem } from 'presentations/api/schema/team_user';
import { FormType } from 'domains/models/form/formType';
import { UserListFilter } from '../../organisms/UserListFilterNavigation';
import {
  isAndroid,
  isiOSApp as isiOSAppFunc,
} from 'presentations/views/helpers/electron';
import { Status } from 'domains/models/form/status';
import { SubscriptionStateItem } from 'presentations/api/schema/subscription';
import FormExportFeature from 'domains/models/subscription/features/form_export';
import RoutingFormFeature from 'domains/models/subscription/features/routing_form';

interface Props {
  viewer: ViewerItem;
}

const perPage = 20;

export function useDashboardFormListPage(props: Props) {
  const t = translateInComponent(translations);
  const router = useRouter();
  const timeZone = currentTimeZone();

  const { dispatch: navigationDispatch } = useContext(DashboardPortalContext);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [deleteForm, setDeleteForm] = useState<FormListItem | null>(null);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [isShowingPlanUpgrade, setIsShowingPlanUpgrade] = useState<boolean>(
    false,
  );

  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [anchorShare, setAnchorShare] = useState<null | HTMLElement>(null);
  const [anchorForm, setAnchorForm] = useState<null | FormListItem>(null);
  const [copyForm, setCopyForm] = useState<FormListItem | null>(null);
  const [embedForm, setEmbedForm] = useState<FormListItem | null>(null);

  const userProfileRequest = useRequest(apiPath('/v1/user/profile'));
  const userProfile: UserProfileItem | null = userProfileRequest.data || null;

  const onCloseMenu = () => {
    setAnchor(null);
    setAnchorForm(null);
  };

  const onClickMenu = (e: any, form: FormListItem) => {
    e.preventDefault();
    setAnchor(e.currentTarget);
    setAnchorForm(form);
  };

  const onClickShareMenu = (
    e: SyntheticEvent<HTMLAnchorElement>,
    form: FormListItem,
  ) => {
    e.preventDefault();
    setAnchorShare(e.currentTarget);
    setAnchorForm(form);
  };

  const onCloseShareMenu = () => {
    setAnchorShare(null);
    setAnchorForm(null);
  };

  const onSelectMenu = async (selectedIndex: number) => {
    if (!anchorForm) return;
    setAnchor(null);
    switch (selectedIndex) {
      case FormOptionIndex.edit: {
        router.push(formEditPath(anchorForm.uid));
        break;
      }
      case FormOptionIndex.copy: {
        if (canAddForm) {
          setCopyForm(anchorForm);
        } else {
          // 予約タイプ数がプラン上限ならダイアログ出す
          setIsShowingPlanUpgrade(true);
        }
        break;
      }
      case FormOptionIndex.delete: {
        setDeleteForm(anchorForm);
        break;
      }
      case FormOptionIndex.activation: {
        if (!subscriptionState) return;

        // 無効から変えようとしている＆有効な予約タイプ数がプラン上限ならダイアログ出す
        if (anchorForm.status === Status.Disable && !canAddForm) {
          setIsShowingPlanUpgrade(true);
          return;
        }

        // プラン内なら有効無効切り替え
        onSwitchActivation(anchorForm);
        break;
      }
      default:
        break;
    }
  };

  const onSwitchActivation = async (form: FormListItem) => {
    const newStatus =
      form.status === Status.Enable ? Status.Disable : Status.Enable;
    const data: FormUpdateBody = {
      status: newStatus,
    };
    const result = await patch(
      apiPath(`/v1/teams/${props.viewer.team!.uid}/forms/${form.uid}/update`),
      { data },
    );

    if (!result.isSuccess) {
      navigationDispatch(setErrorMessage(t('Failed Save Form')));
      return;
    }

    navigationDispatch(
      setSuccessMessage(
        t(
          newStatus === Status.Enable
            ? 'Activation Success'
            : 'Deactivation Success',
        ),
      ),
    );
    formsRequest.revalidate();
    subscriptionStateRequest.revalidate();
  };

  const onSelectShareMenu = async (selectedIndex: number) => {
    if (!anchorForm) return;
    setAnchorShare(null);
    switch (selectedIndex) {
      case FormShareOptionIndex.addToWebsite: {
        setEmbedForm(anchorForm);
        break;
      }
      case FormShareOptionIndex.export: {
        const formExportFeature = new FormExportFeature({
          planVersion: props.viewer.planVersion,
        });
        if (formExportFeature.isValid(props.viewer.planId)) {
          if (isiOSApp) {
            const link = dashboardPath();
            if (
              typeof window.webkit?.messageHandlers?.requestToOpenBrowser
                ?.postMessage === 'function'
            ) {
              window.webkit.messageHandlers.requestToOpenBrowser.postMessage(
                webPath(
                  loginPath({ to: link, redirectIfLoggedIn: true }),
                  router.locale,
                ),
              );
            }
            return;
          }

          const csvExportUrl = dashboardFormAnswerListExportPath({
            teamId: props.viewer.team!.uid,
            formId: anchorForm.uid,
            timeZone,
          });
          window.location.href = csvExportUrl;
        } else {
          setIsShowingPlanUpgrade(true);
        }
        break;
      }
      case FormShareOptionIndex.share: {
        const url = webPath(
          formPath(props.viewer.team!.slug, anchorForm.uid),
          router.locale,
        );
        if (
          typeof window.webkit?.messageHandlers?.showShareSheet?.postMessage ===
          'function'
        ) {
          window.webkit.messageHandlers.showShareSheet.postMessage({
            text: anchorForm.name,
            url,
          });
        } else if (typeof window.navigator.share === 'function') {
          navigator.share({
            title: translate('siteName'),
            text: anchorForm.name,
            url,
          });
        }
        break;
      }
    }
  };

  const isiOSApp = isiOSAppFunc();

  let currentUserId: string | null = null;
  if (router.query.user) {
    const userId = String(router.query.user);
    currentUserId = userId;
  }
  const projectUid = router.query.project ? String(router.query.project) : null;
  const formsRequest = useRequest(
    apiPath(`/v1/teams/${props.viewer.team!.uid}/forms`, {
      userId: currentUserId || undefined,
      projectId: projectUid || undefined,
      formType: FormType.Form,
      sortType: SortType.RecentUsed,
      page: currentPage,
      perPage,
    }),
  );
  const forms: FormListItem[] = formsRequest.data || [];

  const teamUserRequest = useRequest(
    currentUserId === 'all' || projectUid
      ? null
      : apiPath(
          `/v1/teams/${props.viewer.team!.uid}/users/${
            currentUserId ?? props.viewer.uid
          }`,
        ),
  );
  const teamUser: TeamUserItem = teamUserRequest.data || null;

  const subscriptionStateRequest = useRequest(
    props.viewer.team
      ? apiPath(`/v1/teams/${props.viewer.team!.uid}/subscriptions/state`)
      : null,
    { revalidateOnFocus: false },
  );
  const subscriptionState: SubscriptionStateItem | null =
    subscriptionStateRequest.data;
  const routingFormFeature = new RoutingFormFeature({
    planVersion: props.viewer.planVersion,
  });
  const canAddForm =
    subscriptionState &&
    routingFormFeature.canAdd(
      props.viewer.planId,
      subscriptionState.enabledRoutingFormCount,
    );

  const onChangePage = (e: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const onDeleteForm = async () => {
    if (!deleteForm) return;
    setIsConfirmLoading(true);
    const result = await destroy(
      apiPath(
        `/v1/teams/${props.viewer.team!.uid}/forms/${deleteForm.uid}/delete`,
      ),
    );
    if (!result.isSuccess) {
      setDeleteForm(null);
      setIsConfirmLoading(false);
      return;
    }
    setEvent('form delete');

    const newForms = forms.filter((e) => e.id !== deleteForm.id);
    formsRequest.mutate({ ...formsRequest, data: newForms }, false);
    setDeleteForm(null);
    setIsConfirmLoading(false);
  };

  const onCloseDialog = () => {
    setDeleteForm(null);
    setCopyForm(null);
  };

  const onClosePlanUpgrade = () => {
    setIsShowingPlanUpgrade(false);
  };

  const currentTeamUser = useMemo(() => {
    return teamUser || null;
  }, [currentUserId, teamUser]);

  const onClickNewForm = async (e: SyntheticEvent<HTMLButtonElement>) => {
    if (canAddForm) {
      router.push(formNewPath(projectUid));
    } else {
      setIsShowingPlanUpgrade(true);
    }
    setEvent('form click new form', {
      planId: props.viewer.uid,
    });
  };

  const onChangeFilter = (filters: UserListFilter) => {
    const { userId, projectId } = filters;
    const user = props.viewer.uid === userId ? null : userId;
    setCurrentPage(1);
    router.push(dashboardFormListPath({ userId, projectId }));
  };

  const onCopyForm = async () => {
    if (!copyForm) return;
    setIsConfirmLoading(true);
    let result = await post(
      apiPath(`/v1/teams/${props.viewer.team!.uid}/forms/${copyForm.id}/copy`),
    );
    if (!result.isSuccess) {
      setIsConfirmLoading(false);
      navigationDispatch(setErrorMessage(t('Failed Copy Form')));
      return;
    }

    const createBody: FormCreateResult = result.data;
    result = await get(
      apiPath(`/v1/teams/${props.viewer.team!.uid}/forms/${createBody.uid}`),
    );
    if (!result.isSuccess) {
      setIsConfirmLoading(false);
      navigationDispatch(setErrorMessage(t('Failed Copy Event Type')));
      return;
    }
    const newForm: FormListItem = result.data;
    formsRequest.mutate(
      {
        ...formsRequest,
        data: [newForm, ...forms],
      },
      false,
    );
    setCopyForm(null);
    setIsConfirmLoading(false);
  };

  const onCloseAddToWebsite = () => {
    setEmbedForm(null);
  };

  return {
    state: {
      isLoading: formsRequest.isLoading,
      currentPage,
      numberOfPages: formsRequest.numberOfPages,
      currentUserId,
      forms,
      deleteForm,
      isConfirmLoading,
      locale: router.locale,
      query: router.query,
      timeZone,
      projectUid,
      isShowingPlanUpgrade,
      isInitialized: true,
      currentTeamUser,
      anchor,
      anchorForm,
      isiOSApp,
      isAndroid,
      anchorShare,
      copyForm,
      embedForm,
      userProfile,
    },
    actions: {
      onChangePage,
      onSelectMenu,
      onDeleteForm,
      onCloseDialog,
      onClosePlanUpgrade,
      onClickNewForm,
      onChangeFilter,
      onCloseMenu,
      onClickShareMenu,
      onClickMenu,
      onCloseShareMenu,
      onSelectShareMenu,
      onCopyForm,
      onCloseAddToWebsite,
      onSwitchActivation,
    },
  };
}
